import React, { type CSSProperties, useEffect, useState } from 'react'
import classNames from 'classnames'
import { Spinner } from '../../../../components/Spinner/Spinner'
import { CCQ_INIT_COMPLEX_FROM, CCQ_INIT_COMPLEX_NOTE, CCQ_INIT_TITLE } from './ccqRunCopy'
import styles from './CcqInitState.module.scss'

// Matches ccqCriterionCycle in CcqInitState.module.scss.
const INIT_CRITERION_MS = 3000

const INIT_CRITERIA_DELAY_MS = 1500

interface CcqInitCriteriaProps {
  criteria: string[]
  since: number
}

// Which criterion shows, and how far into its animation, follows from the time since the screen first showed, so a
// remounted screen picks up where the last one was. Each is keyed by its cycle so the CSS animation replays in step.
const CcqInitCriteria: React.FC<CcqInitCriteriaProps> = ({ criteria, since }) => {
  const [now, setNow] = useState(() => Date.now())
  const showingFor = now - since - INIT_CRITERIA_DELAY_MS
  const cycles = criteria.length > 1

  useEffect(() => {
    const nextChangeIn = showingFor < 0 ? -showingFor : cycles ? INIT_CRITERION_MS - (showingFor % INIT_CRITERION_MS) : null
    if (nextChangeIn === null) {
      return
    }
    const timer = setTimeout(() => {
      setNow(Date.now())
    }, nextChangeIn)
    return () => {
      clearTimeout(timer)
    }
  }, [showingFor, cycles])

  if (criteria.length === 0) {
    return null
  }

  const cycle = Math.floor(showingFor / INIT_CRITERION_MS)
  const intoAnimation = cycles ? showingFor % INIT_CRITERION_MS : showingFor

  return (
    // Decorative: the criteria are already in Details, so screen readers skip the rotation.
    <div className={classNames(styles.criterionSlot, 'd-flex align-items-center justify-content-center')} aria-hidden='true'>
      {showingFor < 0 ? null : (
        // A lone criterion has nothing to cycle with, and the cycle ends faded out, so it just fades in and stays.
        <span
          key={cycle}
          className={classNames(
            cycles ? styles.criterionCycle : styles.criterionIn,
            'd-flex align-items-center gap-2 paragraph-small zocks-text-gray-700 text-nowrap',
          )}
          style={{ animationDelay: `-${intoAnimation}ms` }}
        >
          <i className='bi bi-x-diamond-fill zocks-text-info' />
          {criteria[cycle % criteria.length]}
        </span>
      )}
    </div>
  )
}

interface CcqInitStateProps {
  question: string
  criteria: string[]
  isExiting: boolean
  // When an earlier init screen of this search first showed (epoch ms), so this one carries on from it.
  shownSince?: number
}

export const CcqInitState: React.FC<CcqInitStateProps> = ({ question, criteria, isExiting, shownSince }) => {
  const [since] = useState(() => shownSince ?? Date.now())
  const [elapsed] = useState(() => Date.now() - since)
  const note = criteria.length >= CCQ_INIT_COMPLEX_FROM ? `${CCQ_INIT_TITLE}. ${CCQ_INIT_COMPLEX_NOTE}` : CCQ_INIT_TITLE

  return (
    <div
      className={classNames(styles.initState, 'position-absolute top-0 start-0 end-0 d-flex flex-column align-items-center justify-content-center')}
      role={isExiting ? undefined : 'status'}
      aria-hidden={isExiting ? true : undefined}
      style={{ '--ccq-init-elapsed': `${elapsed}ms` } as CSSProperties}
    >
      <div className={classNames('d-flex flex-column align-items-center gap-3 text-center', isExiting ? styles.initExit : styles.initEnter)}>
        <span className={classNames(styles.initSpinner, 'd-inline-flex')}>
          <Spinner />
        </span>
        <div className='d-flex flex-column align-items-center gap-1'>
          <h1 className={classNames(styles.initQuestion, 'paragraph-large bold zocks-text-gray-900 m-0')}>{question}</h1>
          <span className='paragraph-small zocks-text-gray-600'>{note}</span>
        </div>
        <CcqInitCriteria criteria={criteria} since={since} />
      </div>
    </div>
  )
}

interface CcqInitPageProps {
  question: string
  criteria: string[]
  shownSince: number
}

// The init screen where the run view would put it, for while there's no execution to give the run view yet.
export const CcqInitPage: React.FC<CcqInitPageProps> = ({ question, criteria, shownSince }) => (
  <div className='truly-full-size-content'>
    <div className='position-relative'>
      <CcqInitState question={question} criteria={criteria} isExiting={false} shownSince={shownSince} />
    </div>
  </div>
)
